import { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import { backgroundImageUrl } from "../services/utilities";

/**
 * TT-962 — manual background picker for the Live Tap page. A filterable contact
 * sheet of the eligible (selected + published + renderable-layout) backgrounds
 * the vendored renderer publishes; clicking one pins it as the manual background
 * (same effect as the Ctrl/Cmd+Shift+1..9 hotkeys, for any eligible background).
 * When a pin is active, an "Auto background selection" button clears it
 * (equivalent to Ctrl/Cmd+Shift+0).
 *
 * Presentational + host-owned: the renderer owns the pin state and hands the
 * eligible list up via onEligibleBackgroundsChange; this modal reads that list
 * and calls back. Mirrors the create-a-Tapestry background picker (useSelectLayout).
 * A background holds its symbols by `primarySymbolId` and `symbolIds`; it is
 * labelled and searched by their names (`nameOf`, the host's useSymbolName).
 */
const imageStyles = {
  width: "200px",
  boxSizing: "border-box",
  cursor: "pointer",
  position: "relative",
  "& img": {
    width: "200px",
    display: "block",
  },
  "&:hover": {
    opacity: 0.75,
  },
};

const styles = {
  titleRow: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
  },
  filter: {
    marginLeft: "auto",
    maxWidth: "240px",
    "& .MuiOutlinedInput-root": { paddingLeft: "10px" },
  },
  closeBtn: {
    // A normal flex child (not absolutely positioned) so it stays vertically
    // centered with the search input; the extra left margin adds breathing room
    // beyond the row's own gap.
    marginLeft: "4px",
    color: "var(--color-text-secondary)",
  },
  autoRow: {
    marginBottom: "12px",
  },
  imageSelect: {
    display: "flex",
    flexWrap: "wrap",
    gap: "12px",
  },
  image: imageStyles,
  selectedImage: {
    ...imageStyles,
    "& img": {
      width: "200px",
      display: "block",
      outline: "4px solid var(--color-accent-primary)",
    },
  },
  empty: {
    color: "var(--color-text-muted)",
    fontStyle: "italic",
  },
};

export default function LiveBackgroundModal({
  open,
  onClose,
  backgrounds = [],
  forcedBackgroundId = null,
  onSelect,
  onAuto,
  nameOf,
}) {
  const [filter, setFilter] = useState("");
  const primaryName = (bg) => (bg.primarySymbolId ? nameOf(bg.primarySymbolId) : "");

  // Reset the search each time the modal opens.
  useEffect(() => {
    if (open) setFilter("");
  }, [open]);

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    const matches = (s) => typeof s === "string" && s.toLowerCase().includes(q);
    const list = !q
      ? backgrounds
      : backgrounds.filter(
          (b) =>
            matches(nameOf(b.primarySymbolId)) ||
            matches(b.layoutName) ||
            (b.symbolIds || []).some((symbolId) => matches(nameOf(symbolId))) ||
            (b.styles || []).some(matches)
        );
    // Surface progressive multi-piece backgrounds first; stable sort preserves the
    // catalog's order within each group.
    return list.slice().sort((a, b) => (b.hasPieces ? 1 : 0) - (a.hasPieces ? 1 : 0));
  }, [backgrounds, filter, nameOf]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={styles.titleRow}>
        <span>Choose a background</span>
        <TextField
          sx={styles.filter}
          size="small"
          placeholder="Search backgrounds..."
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 16, color: "var(--color-text-tertiary)" }} />
              </InputAdornment>
            ),
          }}
        />
        <IconButton onClick={onClose} sx={styles.closeBtn} aria-label="close">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {forcedBackgroundId && (
          <Box sx={styles.autoRow}>
            <Button
              startIcon={<AutoAwesomeIcon />}
              variant="outlined"
              size="small"
              onClick={() => {
                onAuto();
                onClose();
              }}
            >
              Auto background selection
            </Button>
          </Box>
        )}

        {!backgrounds.length ? (
          <Typography sx={styles.empty}>No selectable backgrounds available.</Typography>
        ) : !filtered.length ? (
          <Typography sx={styles.empty}>{`No backgrounds match "${filter}".`}</Typography>
        ) : (
          <Box sx={styles.imageSelect}>
            {filtered.map((bg) => (
              <Box
                key={bg.id}
                sx={bg.id === forcedBackgroundId ? styles.selectedImage : styles.image}
                onClick={() => {
                  onSelect(bg.id);
                  onClose();
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    onSelect(bg.id);
                    onClose();
                  }
                }}
                tabIndex={0}
                role="button">
                <img
                  src={bg.imageUrl || backgroundImageUrl(bg.id)}
                  alt={primaryName(bg) || bg.id}
                  draggable="false"
                />
              </Box>
            ))}
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
}

LiveBackgroundModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  backgrounds: PropTypes.array,
  forcedBackgroundId: PropTypes.string,
  onSelect: PropTypes.func.isRequired,
  onAuto: PropTypes.func.isRequired,
  // symbol_id -> its name (useSymbolName)
  nameOf: PropTypes.func.isRequired,
};
